import React, { useEffect, useState } from "react";
import { Download, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const Stat = ({ label, value, sub, testId }) => (
  <div className="border border-slate-200 px-4 py-3 bg-white" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-heading font-black text-2xl" style={{ color: "var(--hi-olive)" }}>{value?.toLocaleString("en-GB") ?? "—"}</div>
    {sub && <div className="text-[11px] text-slate-600 mt-0.5">{sub}</div>}
  </div>
);

const downloadWorkbook = async (setBusy) => {
  setBusy(true);
  try {
    const res = await fetch(`${API_BASE}/reports/cross-check.xlsx`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("epc_token")}` }, credentials: "include",
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const m = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") || "");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = m ? m[1] : "Cross-check.xlsx";
    a.click();
    toast.success("Cross-check workbook downloaded");
  } catch (e) {
    toast.error("Cross-check workbook failed", { description: e.message });
  } finally {
    setBusy(false);
  }
};

export const CrossCheckCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/reports/cross-check/summary").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const asanaAt = s.asana.finished_at ? new Date(s.asana.finished_at).toLocaleString("en-GB") : "never";
  return (
    <Card className="rounded-none p-6 border-2" style={{ borderColor: "var(--hi-olive)" }} data-testid="cross-check-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--hi-orange)" }}>
            <ShieldCheck className="h-4 w-4" /> Cross-check before you send
          </div>
          <p className="text-sm text-slate-700 mt-2 max-w-2xl">
            One Excel workbook with every property, which list it lands in and why, every Asana task and the property it matched,
            plus a tab for each accuracy check. Asana is re-checked live when you download.
          </p>
        </div>
        <Button onClick={() => downloadWorkbook(setBusy)} disabled={busy} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="cross-check-download-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download cross-check (.xlsx)
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {s.lists.map((l) => (
          <Stat key={l.name} label={l.name.replace("Programme: ", "")} value={l.count} testId={`cross-check-list-${l.name.split(": ")[1].replace(/\s+/g, "-").toLowerCase()}`}
            sub={<span className="inline-flex items-center gap-1">{l.verified ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <XCircle className="h-3 w-3 text-red-600" />}{l.verified ? "re-count matches" : "re-count MISMATCH"}</span>} />
        ))}
        <Stat label="In Asana (excluded)" value={s.asana.properties_linked} sub={`Synced ${asanaAt}`} testId="cross-check-in-asana" />
      </div>
      <div className="mt-4 grid md:grid-cols-5 gap-2" data-testid="cross-check-issues">
        {s.issues.map((i) => (
          <div key={i.key} className="text-xs border border-slate-200 px-3 py-2 bg-slate-50" data-testid={`cross-check-issue-${i.key}`}>
            <span className="font-bold">{i.key}.</span> {i.label}
            <div className="font-mono mt-1"><b>{i.in_lists}</b> in lists · {i.total} overall</div>
          </div>
        ))}
      </div>
    </Card>
  );
};
